/* ===========================
   Wiredan Vault Protocol - Stable Release 10.9
   Path: src/components/BottomNav.jsx
   Upgrades: Enlarged Icons + Numeric Green Badges + Live Marketplace Freeze Interceptor
   =========================== */

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  FiShoppingBag, 
  FiLayout, 
  FiPlusCircle, 
  FiSettings, 
  FiHome 
} from 'react-icons/fi';

const BottomNav = () => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();

  const [marketActive, setMarketActive] = useState(true);

  // Fetch live system status from the Omni-Engine root to check freeze toggle state
  useEffect(() => {
    async function checkMarketState() {
      try {
        const res = await fetch('/');
        const data = await res.json();
        if (data && data.marketplace_status === "HALTED") {
          setMarketActive(false);
        } else {
          setMarketActive(true);
        }
      } catch (err) {
        // Defaults to active if network fails temporarily
      }
    }
    checkMarketState();
  }, []);

  // Helper to check if a link is active
  const isActive = (path) => location.pathname === path;

  // Root Request: Fetch unread counts (mocked from user state or context)
  const vaultCount = user?.vault_notif_count || 0; 
  const orderCount = user?.order_notif_count || 0;

  // Handler to block navigation and alert user if market is frozen
  const handleMarketClick = (e) => {
    if (!marketActive) {
      e.preventDefault();
      alert("Marketplace activity is currently halted by admin. Trading and listings are frozen.");
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 px-6 py-4 flex justify-between items-center z-50 pb-safe shadow-[0_-4px_25px_rgba(0,0,0,0.06)]">
      
      {/* Home / Marketplace (Blocked if market is frozen) */}
      <Link 
        to="/marketplace" 
        onClick={handleMarketClick}
        className={`flex flex-col items-center gap-1.5 ${!marketActive ? 'opacity-40 cursor-not-allowed' : ''} ${isActive('/marketplace') ? 'text-[#F25C05]' : 'text-slate-400'}`}
      >
        <FiHome size={28} strokeWidth={2.5} /> {/* ENLARGED */}
        <span className="text-[9px] font-black uppercase tracking-tighter">
          {marketActive ? 'Market' : 'Frozen'}
        </span>
      </Link>

      {/* Seller Dashboard / Vault */}
      {isAuthenticated && (
        <Link to="/seller/dashboard" className={`relative flex flex-col items-center gap-1.5 ${isActive('/seller/dashboard') ? 'text-[#F25C05]' : 'text-slate-400'}`}>
          <FiLayout size={28} strokeWidth={2.5} /> {/* ENLARGED */}
          
          {/* ROOT REQUEST: GREEN NUMERIC BADGE */}
          {vaultCount > 0 && (
            <span className="absolute -top-1 -right-2 bg-[#00FF00] text-black text-[10px] font-black px-1.5 py-0.5 rounded-full border border-white shadow-sm min-w-[18px] text-center">
              {vaultCount}
            </span>
          )}
          
          <span className="text-[9px] font-black uppercase tracking-tighter">Vault</span>
        </Link>
      )}

      {/* FAB (Floating Action Button) - Create Listing (Blocked if market is frozen) */}
      <Link 
        to="/marketplace/create" 
        onClick={handleMarketClick}
        className={`relative -mt-14 ${!marketActive ? 'opacity-40 cursor-not-allowed pointer-events-none' : ''}`}
      >
        <div className="bg-[#F25C05] p-5 rounded-full text-white shadow-2xl shadow-[#F25C05]/50 border-[7px] border-white active:scale-90 transition-transform">
          <FiPlusCircle size={32} strokeWidth={2.5} /> {/* ENLARGED */}
        </div>
      </Link>

      {/* Orders / Tracking */}
      <Link to="/order-tracking" className={`relative flex flex-col items-center gap-1.5 ${isActive('/order-tracking') || isActive('/orders') ? 'text-[#F25C05]' : 'text-slate-400'}`}>
        <FiShoppingBag size={28} strokeWidth={2.5} /> {/* ENLARGED */}
        
        {/* ROOT REQUEST: GREEN NUMERIC BADGE */}
        {orderCount > 0 && (
          <span className="absolute -top-1 -right-2 bg-[#00FF00] text-black text-[10px] font-black px-1.5 py-0.5 rounded-full border border-white shadow-sm min-w-[18px] text-center">
            {orderCount}
          </span>
        )}
        
        <span className="text-[9px] font-black uppercase tracking-tighter">Orders</span>
      </Link>

      {/* Settings / Node Config */}
      <Link to="/settings" className={`flex flex-col items-center gap-1.5 ${isActive('/settings') ? 'text-[#F25C05]' : 'text-slate-400'}`}>
        <FiSettings size={28} strokeWidth={2.5} /> {/* ENLARGED */}
        <span className="text-[9px] font-black uppercase tracking-tighter">Node</span>
      </Link>
    </div>
  );
};

export default BottomNav;